import type { Chat } from '../../services/chatService/ChatService';
import { dateRangeLabel, guestSummary } from '../StayGuestPage/staySearchState';

/**
 * How a stay's chat reads in the inbox and in the chat header (spec 016 W27). A chat is a
 * stay's when the server stamped a `kind` or a `listingId` on it; car chats keep their
 * "Booking #" title and car fields.
 */
type StayChatFields = Pick<Chat, 'kind' | 'listingId' | 'listingName' | 'inquiry' | 'bookingId'>;

export function isStayChat(chat: Pick<Chat, 'kind' | 'listingId'>): boolean {
  return Boolean(chat.kind || chat.listingId);
}

/** An inquiry has no booking yet; it becomes a reservation chat when a booking joins it. */
export function isInquiry(chat: Pick<Chat, 'kind' | 'bookingId'>): boolean {
  return chat.kind === 'inquiry' && !chat.bookingId;
}

export function stayChatTitle(chat: Pick<Chat, 'listingName'>): string {
  return chat.listingName?.trim() || 'Place to stay';
}

/** "Mar 20 to 23 · 2 guests", or null when the guest named neither dates nor guests. */
export function inquirySummary(inquiry: Chat['inquiry']): string | null {
  if (!inquiry) return null;
  const parts: string[] = [];
  if (inquiry.checkIn && inquiry.checkOut)
    parts.push(dateRangeLabel(inquiry.checkIn, inquiry.checkOut));
  if (inquiry.guests) {
    parts.push(
      guestSummary({
        adults: inquiry.guests.adults ?? 1,
        children: inquiry.guests.children ?? 0,
        infants: inquiry.guests.infants ?? 0,
        pets: inquiry.guests.pets ?? 0,
      }),
    );
  }
  return parts.length ? parts.join(' · ') : null;
}

/** The small line under the title: who, and whether it is a question or a reservation. */
export function stayChatSubtitle(chat: StayChatFields, otherName: string): string {
  if (isInquiry(chat)) {
    return [otherName, 'Inquiry', inquirySummary(chat.inquiry)].filter(Boolean).join(' · ');
  }
  return [otherName, 'Reservation'].join(' · ');
}
